import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Drawer as DrawerPrimitive } from "vaul";
import { X } from "lucide-react";
import { cn, useMediaQuery } from "@jammable/ui-core";

// Credenza's pattern (a dialog on desktop, a draggable vaul drawer on
// phones) in design language F: lavender-ink scrim, 32px radius, no
// borders or shadows. The open/close motion comes from tailwindcss-animate
// (dialog) and vaul (drawer). Used by the /create studio's pickers.

interface JamSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  // Pinned under the scrolling body, e.g. a Done button.
  footer?: React.ReactNode;
  className?: string;
  // z-index to open at, for a sheet opened from inside a ModalContext modal
  // (those stack from 10000). Default: the classes' 500/501.
  layer?: number;
  // false: no close button, and Escape, a tap outside or a drag down don't
  // close it (the card wall, which blocks the site until they act).
  closable?: boolean;
  // "dark": the purple-to-ink upgrade sheet (cover upsells), white text.
  tone?: "light" | "dark";
  // A small lime label above the title.
  eyebrow?: string;
}

const DARK_SHEET =
  "tw-bg-[radial-gradient(120%_70%_at_100%_0%,#8B3FE0_0%,#4A2380_45%,#2E0F52_100%)] tw-text-white";

const Header = ({
  title,
  Title,
  onClose,
  dark,
  eyebrow,
}: {
  title: string;
  Title: typeof DialogPrimitive.Title | typeof DrawerPrimitive.Title;
  onClose?: () => void;
  dark?: boolean;
  eyebrow?: string;
}) => (
  <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1.5">
      {eyebrow && (
        <span className="tw-text-[11px] tw-font-extrabold tw-uppercase tw-tracking-[0.08em] tw-text-jam-lime">
          {eyebrow}
        </span>
      )}
      <Title
        className={cn(
          "jam-display tw-m-0 tw-text-[24px] tw-font-extrabold",
          dark
            ? "tw-text-[28px] tw-leading-[1.02] tw-text-white"
            : "tw-text-jam-ink",
        )}
      >
        {title}
      </Title>
    </div>
    {onClose && (
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={cn(
          "tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-cursor-pointer tw-items-center tw-justify-center tw-self-start tw-rounded-full tw-border-0 tw-transition-colors",
          dark
            ? "tw-bg-white/10 tw-text-white hover:tw-bg-white/20"
            : "tw-bg-jam-lav tw-text-jam-ink hover:tw-bg-jam-line",
        )}
      >
        <X size={18} strokeWidth={2.5} />
      </button>
    )}
  </div>
);

const Body = ({ children }: { children: React.ReactNode }) => (
  <div className="tw-flex tw-min-h-0 tw-flex-col tw-gap-3.5 tw-overflow-y-auto tw-overscroll-contain">
    {children}
  </div>
);

export const JamSheet = ({
  open,
  onOpenChange,
  title,
  children,
  footer,
  className,
  layer,
  closable = true,
  tone = "light",
  eyebrow,
}: JamSheetProps) => {
  const dark = tone === "dark";
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const close = () => onOpenChange(false);
  const change = (o: boolean) => (o || closable) && onOpenChange(o);
  const overlayZ = layer ? { zIndex: layer } : undefined;
  const contentZ = layer ? { zIndex: layer + 1 } : undefined;

  if (isDesktop) {
    return (
      <DialogPrimitive.Root open={open} onOpenChange={change}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className="tw-fixed tw-inset-0 tw-z-[500] tw-bg-jam-ink/40 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0"
            style={overlayZ}
          />
          {/* Centred by this flex wrapper, not translate(-50%): the
              zoom/slide animation drives `transform`, which would override a
              centring translate and make the dialog fly in from a corner. */}
          <div
            className="tw-pointer-events-none tw-fixed tw-inset-0 tw-z-[501] tw-flex tw-items-center tw-justify-center tw-p-6"
            style={contentZ}
          >
            <DialogPrimitive.Content
              aria-describedby={undefined}
              className={cn(
                "tw-pointer-events-auto tw-flex tw-max-h-[82vh] tw-w-full tw-max-w-[640px] tw-flex-col tw-gap-4 tw-rounded-[32px] tw-p-6 focus:tw-outline-none",
                dark ? DARK_SHEET : "tw-bg-white tw-text-jam-ink",
                "tw-duration-200 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95 data-[state=open]:tw-slide-in-from-bottom-2",
                className,
              )}
            >
              <Header
                title={title}
                Title={DialogPrimitive.Title}
                onClose={closable ? close : undefined}
                dark={dark}
                eyebrow={eyebrow}
              />
              <Body>{children}</Body>
              {footer}
            </DialogPrimitive.Content>
          </div>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }

  return (
    <DrawerPrimitive.Root
      open={open}
      onOpenChange={change}
      dismissible={closable}
    >
      <DrawerPrimitive.Portal>
        <DrawerPrimitive.Overlay
          className="tw-fixed tw-inset-0 tw-z-[500] tw-bg-jam-ink/40"
          style={overlayZ}
        />
        <DrawerPrimitive.Content
          aria-describedby={undefined}
          style={contentZ}
          className={cn(
            "tw-fixed tw-inset-x-0 tw-bottom-0 tw-z-[501] tw-flex tw-max-h-[92vh] tw-flex-col tw-gap-4 tw-rounded-t-[32px] tw-px-4 tw-pb-5 focus:tw-outline-none",
            dark ? DARK_SHEET : "tw-bg-white tw-text-jam-ink",
            className,
          )}
        >
          <div
            className={cn(
              "tw-mx-auto tw-mt-3 tw-h-1.5 tw-w-12 tw-shrink-0 tw-rounded-full",
              dark ? "tw-bg-white/30" : "tw-bg-jam-line",
            )}
          />
          <Header
            title={title}
            Title={DrawerPrimitive.Title}
            onClose={closable ? close : undefined}
            dark={dark}
            eyebrow={eyebrow}
          />
          <Body>{children}</Body>
          {footer}
        </DrawerPrimitive.Content>
      </DrawerPrimitive.Portal>
    </DrawerPrimitive.Root>
  );
};
